import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Truck, XCircle, PackageCheck, Pencil, Printer, Search, Trash2, Camera, FileText, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";
import { printShippingLabel, printPackingSlip } from "../../lib/printDocs";
import { StatusBadge } from "../../components/StatusBadge";
import { OrderNotesButton } from "./OrderNotes";
import { SortableHead } from "../../components/SortableHead";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const FILTERS = ["pending", "verified", "shipped", "completed", "cancelled", "all"];
const FUNNEL = ["pending", "verified", "shipped", "completed"];

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [dialogOrder, setDialogOrder] = useState(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanFileRef = useRef(null);
  const [printPromptOrder, setPrintPromptOrder] = useState(null);
  const [shipFrom, setShipFrom] = useState("");
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [search, setSearch] = useState(initialQ);
  const [q, setQ] = useState(initialQ);
  const [sort, setSort] = useState("date_desc");
  const [selected, setSelected] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notesSummary, setNotesSummary] = useState({});
  const { user } = useAuth();
  const canDelete = !!(user?.is_super_admin || user?.is_general_admin);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setShipFrom(data.ship_from || "")).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort };
    if (q) {
      params.q = q; // search across all statuses
    } else if (filter !== "all") {
      params.status = filter;
    }
    setSelected(new Set());
    api.get("/admin/orders", { params })
      .then(({ data }) => setOrders(data))
      .catch(() => toast.error("Failed to load orders"))
      .finally(() => setLoading(false));
    api.get("/admin/orders/notes-summary").then(({ data }) => setNotesSummary(data)).catch(() => {});
  }, [filter, sort, q]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))));
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget.length === 1) {
        await api.delete(`/admin/orders/${deleteTarget[0]}`);
        toast.success("Order deleted");
      } else {
        const { data } = await api.post("/admin/orders/bulk-delete", { order_ids: deleteTarget });
        toast.success(`${data.deleted} orders deleted`);
      }
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete orders");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const sortField = sort.replace(/_(asc|desc)$/, "");
  const sortDir = sort.endsWith("_asc") ? "asc" : "desc";
  const headerSort = (field) =>
    setSort(sortField === field ? `${field}_${sortDir === "asc" ? "desc" : "asc"}` : `${field}_asc`);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status}`);
      load();
    } catch {
      toast.error("Failed to update order");
    }
  };

  const handleVerify = async (order) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/status`, { status: "verified" });
      toast.success("Order verified");
      load();
      setPrintPromptOrder(data);
    } catch {
      toast.error("Failed to update order");
    }
  };

  const handleScanPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/orders/extract-tracking", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTrackingInput(data.tracking_number);
      toast.success("Tracking number read from photo — confirm and save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the photo — try again or type it manually.");
    } finally {
      setScanning(false);
    }
  };

  const openShipDialog = (order) => {
    setDialogOrder(order);
    setTrackingInput(order.tracking_number || "");
  };

  const confirmShipDialog = async () => {
    setSavingTracking(true);
    try {
      if (dialogOrder.status === "verified") {
        await api.put(`/admin/orders/${dialogOrder.id}/status`, { status: "shipped", tracking_number: trackingInput.trim() });
        toast.success("Order marked as shipped");
      } else {
        await api.put(`/admin/orders/${dialogOrder.id}/tracking`, { tracking_number: trackingInput.trim() });
        toast.success("Tracking number updated");
      }
      setDialogOrder(null);
      load();
    } catch {
      toast.error("Failed to update order");
    } finally {
      setSavingTracking(false);
    }
  };

  let confirmLabel = "Save Tracking";
  if (savingTracking) confirmLabel = "Saving…";
  else if (dialogOrder?.status === "verified") confirmLabel = "Mark as Shipped";

  return (
    <div>
      {filter === "pending" && (
        <div className="border border-[#8C7B6B] bg-[#EAE5DE]/50 rounded-lg p-4 mb-6 text-sm text-[#1A1A1A]" data-testid="admin-pending-hint">
          <strong>Verification:</strong> match the incoming e-Transfer's <strong>exact dollar amount</strong> and <strong>sender email</strong> against the highlighted values below, then click Verify.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`admin-orders-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs tracking-[0.1em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${
              filter === f ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "text-[#6E675F] border-[#DDD7CF] hover:border-[#8C7B6B]"
            }`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#8C7B6B] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-order-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, email, name or tracking…"
            className="pl-9"
          />
          {q && (
            <p className="text-[11px] text-[#8C7B6B] mt-1 absolute -bottom-5 left-1" data-testid="admin-search-all-hint">
              Searching all statuses
            </p>
          )}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="admin-order-sort-select" className="w-full sm:w-[200px] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc" data-testid="admin-order-sort-date-desc">Newest first</SelectItem>
            <SelectItem value="date_asc" data-testid="admin-order-sort-date-asc">Oldest first</SelectItem>
            <SelectItem value="total_desc" data-testid="admin-order-sort-total-desc">Total: high to low</SelectItem>
            <SelectItem value="total_asc" data-testid="admin-order-sort-total-asc">Total: low to high</SelectItem>
            <SelectItem value="order_asc" data-testid="admin-order-sort-order-asc">Order # A–Z</SelectItem>
            <SelectItem value="order_desc" data-testid="admin-order-sort-order-desc">Order # Z–A</SelectItem>
            <SelectItem value="status_asc" data-testid="admin-order-sort-status-asc">Status A–Z</SelectItem>
            <SelectItem value="status_desc" data-testid="admin-order-sort-status-desc">Status Z–A</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {canDelete && selected.size > 0 && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4" data-testid="admin-orders-bulk-bar">
          <span className="text-sm text-red-800 font-medium" data-testid="admin-orders-selected-count">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="admin-orders-clear-selection" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button size="sm" data-testid="admin-orders-delete-selected" onClick={() => setDeleteTarget([...selected])} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && orders.length === 0 && (
        <p className="text-center text-[#6E675F] py-20" data-testid="admin-no-orders">
          {q ? `No orders match "${q}".` : `No ${filter !== "all" ? filter : ""} orders.`}
        </p>
      )}
      {!loading && orders.length > 0 && (
        <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
          <Table data-testid="admin-orders-table">
            <TableHeader>
              <TableRow>
                {canDelete && (
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="admin-orders-select-all"
                      checked={orders.length > 0 && selected.size === orders.length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all orders"
                    />
                  </TableHead>
                )}
                <SortableHead label="Order" active={sortField === "order"} dir={sortDir} onClick={() => headerSort("order")} testid="orders-sort-order" />
                <SortableHead label="Date" active={sortField === "date"} dir={sortDir} onClick={() => headerSort("date")} testid="orders-sort-date" />
                <TableHead>Match Sender Email</TableHead>
                <SortableHead label="Match Amount" active={sortField === "total"} dir={sortDir} onClick={() => headerSort("total")} testid="orders-sort-amount" />
                <TableHead>Items</TableHead>
                <TableHead>Tracking</TableHead>
                <SortableHead label="Status" active={sortField === "status"} dir={sortDir} onClick={() => headerSort("status")} testid="orders-sort-status" />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`admin-order-row-${o.order_number}`}>
                  {canDelete && (
                    <TableCell className="w-10">
                      <Checkbox
                        data-testid={`admin-order-select-${o.order_number}`}
                        checked={selected.has(o.id)}
                        onCheckedChange={() => toggleSelect(o.id)}
                        aria-label={`Select order ${o.order_number}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{o.order_number}</TableCell>
                  <TableCell className="text-[#6E675F]">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <span className={o.status === "pending" ? "font-semibold bg-[#EAE5DE] px-2 py-1 rounded" : ""} data-testid={`admin-order-email-${o.order_number}`}>
                      {o.email}
                    </span>
                    {o.shipping_address && (
                      <p className="text-xs text-[#8C7B6B] mt-1 flex items-center gap-1.5" data-testid={`admin-order-city-${o.order_number}`}>
                        {o.shipping_address.city}, {o.shipping_address.province}
                        {o.shipping_method === "express" && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold tracking-wide uppercase bg-[#FEF3E2] text-[#B45309] border border-[#B45309]/30 rounded px-1.5 py-0.5" data-testid={`admin-order-express-${o.order_number}`}>
                            <Zap className="w-3 h-3" /> Express
                          </span>
                        )}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={o.status === "pending" ? "font-bold text-[#6B5D50] bg-[#EAE5DE] px-2 py-1 rounded" : "font-medium"} data-testid={`admin-order-amount-${o.order_number}`}>
                      ${o.total.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="text-[#6E675F] max-w-[200px] truncate">
                    {o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm" data-testid={`admin-order-tracking-${o.order_number}`}>
                        {o.tracking_number || "—"}
                      </span>
                      {["shipped", "completed"].includes(o.status) && (
                        <button
                          data-testid={`admin-edit-tracking-button-${o.order_number}`}
                          onClick={() => openShipDialog(o)}
                          className="p-1 text-[#8C7B6B] hover:text-[#1A1A1A] transition-colors shrink-0"
                          aria-label="Edit tracking number"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  </TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {FUNNEL.includes(o.status) && (
                        <div className="flex items-center border border-[#DDD7CF] rounded-md" title="Step this order backward or forward in the funnel">
                          <button
                            data-testid={`admin-order-step-back-${o.order_number}`}
                            disabled={FUNNEL.indexOf(o.status) === 0}
                            onClick={() => updateStatus(o.id, FUNNEL[FUNNEL.indexOf(o.status) - 1])}
                            title={FUNNEL.indexOf(o.status) > 0 ? `Move back to ${FUNNEL[FUNNEL.indexOf(o.status) - 1]}` : "Already at the first step"}
                            className="p-1.5 text-[#6E675F] hover:text-[#1A1A1A] hover:bg-[#EFEBE6] rounded-l-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Move order back one step"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="w-px h-4 bg-[#DDD7CF]" />
                          <button
                            data-testid={`admin-order-step-forward-${o.order_number}`}
                            disabled={FUNNEL.indexOf(o.status) === FUNNEL.length - 1}
                            onClick={() => updateStatus(o.id, FUNNEL[FUNNEL.indexOf(o.status) + 1])}
                            title={FUNNEL.indexOf(o.status) < FUNNEL.length - 1 ? `Advance to ${FUNNEL[FUNNEL.indexOf(o.status) + 1]}` : "Already at the last step"}
                            className="p-1.5 text-[#6E675F] hover:text-[#1A1A1A] hover:bg-[#EFEBE6] rounded-r-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Advance order one step"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <OrderNotesButton
                        order={o}
                        count={notesSummary[o.id] || 0}
                        onCountChange={(n) => setNotesSummary((s) => ({ ...s, [o.id]: n }))}
                      />
                      {o.status === "pending" && (
                        <>
                          <Button size="sm" data-testid={`admin-verify-order-button-${o.order_number}`} onClick={() => handleVerify(o)} className="bg-[#8C7B6B] text-[#1A1A1A] hover:bg-[#75665A]">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Verify
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`admin-cancel-order-button-${o.order_number}`} onClick={() => updateStatus(o.id, "cancelled")} className="text-red-700 border-red-200 hover:bg-red-50">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {["verified", "shipped", "completed"].includes(o.status) && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`print-label-button-${o.order_number}`} onClick={() => printShippingLabel(o, shipFrom)} title="Print shipping label (57×32mm)">
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`print-slip-button-${o.order_number}`} onClick={() => printPackingSlip(o)} title="Print packing slip">
                            <FileText className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {o.status === "verified" && (
                        <Button size="sm" variant="outline" data-testid={`admin-ship-order-button-${o.order_number}`} onClick={() => openShipDialog(o)}>
                          <Truck className="w-4 h-4 mr-1" /> Ship
                        </Button>
                      )}
                      {o.status === "shipped" && (
                        <Button size="sm" variant="outline" data-testid={`admin-complete-order-button-${o.order_number}`} onClick={() => updateStatus(o.id, "completed")}>
                          <PackageCheck className="w-4 h-4 mr-1" /> Complete
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="outline" data-testid={`admin-delete-order-button-${o.order_number}`} onClick={() => setDeleteTarget([o.id])} className="text-red-700 border-red-200 hover:bg-red-50" title="Delete order">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!printPromptOrder} onOpenChange={(open) => !open && setPrintPromptOrder(null)}>
        <DialogContent data-testid="print-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Order Verified</DialogTitle>
            <DialogDescription>
              Print the shipping label (57×32mm) or packing slip for {printPromptOrder?.order_number}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="print-prompt-no" onClick={() => setPrintPromptOrder(null)}>No</Button>
            <Button
              variant="outline"
              data-testid="print-prompt-slip"
              onClick={() => printPackingSlip(printPromptOrder)}
            >
              <FileText className="w-4 h-4 mr-1" /> Packing Slip
            </Button>
            <Button
              data-testid="print-prompt-label"
              onClick={() => printShippingLabel(printPromptOrder, shipFrom)}
              className="bg-[#8C7B6B] text-white hover:bg-[#75665A]"
            >
              <Printer className="w-4 h-4 mr-1" /> Shipping Label
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialogOrder} onOpenChange={(open) => !open && setDialogOrder(null)}>
        <DialogContent data-testid="tracking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {dialogOrder?.status === "verified" ? "Ship Order" : "Edit Tracking"} {dialogOrder?.order_number}
            </DialogTitle>
            <DialogDescription>
              {dialogOrder?.status === "verified"
                ? "Add a tracking number (optional) — the customer will see it in My Orders."
                : "Update the tracking number shown to the customer."}
            </DialogDescription>
          </DialogHeader>
          <div>
            {dialogOrder?.shipping_address && (
              <div className="mb-4 bg-[#F7F5F2] border border-[#DDD7CF] rounded-lg p-4 text-sm text-[#1A1A1A]" data-testid="dialog-shipping-address">
                <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-1.5">Ship To</p>
                <p className="font-medium">{dialogOrder.shipping_address.name}</p>
                <p>{dialogOrder.shipping_address.line1}{dialogOrder.shipping_address.line2 ? `, ${dialogOrder.shipping_address.line2}` : ""}</p>
                <p>{dialogOrder.shipping_address.city}, {dialogOrder.shipping_address.province} {dialogOrder.shipping_address.postal_code}</p>
                {dialogOrder.shipping_address.phone && <p className="text-[#6E675F]">Phone: {dialogOrder.shipping_address.phone}</p>}
                <p className={`mt-2 pt-2 border-t border-[#DDD7CF] text-xs font-semibold ${dialogOrder.shipping_method === "express" ? "text-[#B45309]" : "text-[#6E675F]"}`} data-testid="dialog-shipping-method">
                  Ship via: {dialogOrder.shipping_method === "express" ? "EXPRESS — Canada Post Xpresspost™" : "Standard — Canada Post Expedited Parcel™"}
                </p>
              </div>
            )}
            <Label htmlFor="tracking-number">Tracking Number</Label>
            <Input
              id="tracking-number"
              data-testid="tracking-number-input"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              placeholder="e.g. 1234 5678 9012 CA"
              className="mt-1"
            />
            <input
              ref={scanFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="scan-label-file-input"
              onChange={handleScanPhoto}
            />
            <Button
              variant="outline"
              data-testid="scan-label-button"
              disabled={scanning}
              onClick={() => scanFileRef.current?.click()}
              className="mt-3 w-full border-[#8C7B6B] text-[#8C7B6B] hover:bg-[#8C7B6B] hover:text-white"
            >
              <Camera className="w-4 h-4 mr-2" />
              {scanning ? "Reading photo…" : "Scan from label photo"}
            </Button>
            <p className="text-xs text-[#6E675F] mt-2">
              Snap a photo of the shipping label and the tracking number is filled in automatically.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOrder(null)} data-testid="tracking-dialog-cancel">Cancel</Button>
            <Button
              data-testid="tracking-dialog-confirm"
              onClick={confirmShipDialog}
              disabled={savingTracking || (dialogOrder?.status !== "verified" && !trackingInput.trim())}
              className="bg-[#8C7B6B] text-white hover:bg-[#75665A]"
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-orders-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              Delete {deleteTarget?.length === 1 ? "Order" : `${deleteTarget?.length} Orders`}
            </DialogTitle>
            <DialogDescription>
              This permanently removes {deleteTarget?.length === 1 ? "this order" : "these orders"}, restores any deducted stock and reverses loyalty points earned or redeemed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="delete-orders-cancel" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button data-testid="delete-orders-confirm" onClick={confirmDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-1" /> {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
